import { useMemo } from "react";

import { useAppSelector } from "app/hooks";
import {
  selectCurrentClinic,
  selectCurrentRegion,
  selectCurrentRegionClinicsIds,
  selectCurrentUser,
  selectHasRegions,
  selectUserClinicsIds
} from "features/auth/auth-slice";

import { getIncludeMessagesFromOtherClinicsState } from "../inbox-slice";

export type InboxScope = {
  /** `undefined` = every clinic; `[]` = none resolved yet. */
  clinicFilter: number[] | undefined;
  region: number | undefined;
};

/**
 * The preferred_clinic / region scope of the Inbox dialog list. Shared with the
 * text-routing counts (Inbox tabs + side-menu badge) so a count can never cover
 * conversations the list is unable to show.
 */
export default function useInboxScope(): InboxScope {
  const currentUser = useAppSelector(selectCurrentUser);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentRegion = useAppSelector(selectCurrentRegion);
  const hasRegions = useAppSelector(selectHasRegions);
  const currentRegionClinicsIds = useAppSelector(selectCurrentRegionClinicsIds);
  const userClinicsIds = useAppSelector(selectUserClinicsIds);
  const includeMessagesFromOtherClinics = useAppSelector(
    getIncludeMessagesFromOtherClinicsState
  );

  const clinicFilter = useMemo(() => {
    if (currentUser?.show_own_clinics_only) {
      return userClinicsIds.length > 0 ? userClinicsIds : [];
    }

    return includeMessagesFromOtherClinics
      ? hasRegions
        ? currentRegionClinicsIds.filter(Boolean)
        : undefined
      : currentClinic?.id
        ? [currentClinic.id]
        : [];
  }, [
    currentUser?.show_own_clinics_only,
    userClinicsIds,
    includeMessagesFromOtherClinics,
    hasRegions,
    currentRegionClinicsIds,
    currentClinic?.id
  ]);

  return useMemo(
    () => ({
      clinicFilter,
      region: hasRegions ? currentRegion?.id : undefined
    }),
    [clinicFilter, hasRegions, currentRegion?.id]
  );
}
